import OrgPayment from "@/atomic/organisms/orgPayment";
import Header from "@/atomic/atoms/Header";
import { PropsView } from "./Models";
import Loading from "@/atomic/atoms/Loading";
import LoadingOverlay from "@/atomic/atoms/LoadingOverlay";
import useI18n from "@/hooks/useI18n";
import LanguageSelector from "@/atomic/atoms/LanguageSelector";
import Modal from "@/atomic/atoms/Modal";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import environment from "@/settings/environment";
import mobileBackgroundHome from "@/assets/mobile-background-home.jpg";
import deskBackgroundHome from "@/assets/desk-background-home.png";
import { useThemeContext } from "@/contexts/ThemeContext";

export default function HomeView({
  goToVehicles,
  items,
  plate,
  isLoading,
  onChange,
  selectables,
  goBack,
  goGetout,
  onSearchNewPlate,
  isCaptchaVerified = false,
  isCaptchaMtcCaptcha = false,
  captchaName = "mtcaptcha-home-modal",
  resetCaptcha,
  incrementCaptchaKey,
  isLoadingCaptchaFlag = false,
}: PropsView) {
  const { t } = useI18n();
  const { themeName } = useThemeContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newPlate, setNewPlate] = useState("");
  const [showCaptcha, setShowCaptcha] = useState(false);
  const hasInitializedCaptcha = useRef(false);

  const title = themeName === 'zapay' ? 'Pedágio' : t("pages:home:debts");

  const formattedPlate = useMemo(() => 
    plate ? plate.replace(/^([A-Za-z]{3})(\d[A-Za-z]?\d{2,3})$/, "$1-$2") : "", 
    [plate]
  );

  const isButtonDisabled = useMemo(() => 
    !newPlate || newPlate.replace(/[^A-Z0-9]/g, "").length < 5 || !isCaptchaVerified,
    [newPlate, isCaptchaVerified]
  );

  const buttonClassName = useMemo(() => 
    `w-full h-[calc(60px)] rounded-theme-medium text-white font-medium ${
      isButtonDisabled
        ? "bg-disabled text-disabled-text cursor-not-allowed"
        : "bg-primary hover:opacity-90"
    }`,
    [isButtonDisabled]
  );

  const handleSearchClick = useCallback(() => {
    const cleanPlate = newPlate.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (cleanPlate && cleanPlate.length >= 5 && onSearchNewPlate) {
      onSearchNewPlate(cleanPlate);
      setIsModalOpen(false);
      setNewPlate("");
    }
  }, [newPlate, onSearchNewPlate]);

  const toggleModal = useCallback(() => {
    const wasOpen = isModalOpen;
    setIsModalOpen(!isModalOpen);
    
    if (wasOpen) {
      setShowCaptcha(false);
      setNewPlate("");
      
      if (isCaptchaMtcCaptcha) {
        const element = document.getElementById(captchaName);
        if (element) {
          element.remove();
        }
        if ((window as any)?.mtcaptcha?.renderQueue) {
          const queue = (window as any).mtcaptcha.renderQueue;
          const index = queue.indexOf(captchaName);
          if (index > -1) {
            queue.splice(index, 1);
          }
        }
        try {
          if ((window as any)?.mtcaptcha?.resetUI) {
            (window as any).mtcaptcha.resetUI(captchaName);
          }
        } catch (error) {
        }
      } else {
        const recaptchaContainer = document.querySelector('.g-recaptcha');
        if (recaptchaContainer?.parentElement) {
          recaptchaContainer.parentElement.innerHTML = '';
        }
        if ((window as any).grecaptcha?.enterprise?.reset) {
          (window as any).grecaptcha.enterprise.reset();
        } else if ((window as any).grecaptcha?.reset) {
          (window as any).grecaptcha.reset();
        }
        (window as any).recaptchaToken = null;
      }
      
      resetCaptcha?.();
    } else {
      setNewPlate("");
    }
  }, [isModalOpen, isCaptchaMtcCaptcha, captchaName, resetCaptcha]);

  useEffect(() => {
    if (isModalOpen) {
      if (!hasInitializedCaptcha.current) {
        incrementCaptchaKey?.();
        hasInitializedCaptcha.current = true;
      }
      
      const timer = setTimeout(() => {
        setShowCaptcha(true);
        resetCaptcha?.();
      }, 100);
      
      return () => {
        clearTimeout(timer);
      };
    } else {
      setShowCaptcha(false);
      hasInitializedCaptcha.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  return (
    <div className="relative flex flex-1 flex-col w-full">
      <Header
        title={title}
        hasLeftButton
        goBack={goGetout}
        rightButton={
          <LanguageSelector
            className="text-text"
            arrowImg="/arrow-bottom.png"
            showFlag={true}
          />
        }
      />
      <div className="absolute top-[calc(72px)] left-0 right-0 w-full h-[calc(220px)] bg-foreground z-0">
        {themeName !== 'zapay' && (
          <>
            <img
              src={mobileBackgroundHome}
              alt="Background"
              className="absolute block md:hidden object-cover object-[25%_35%] h-[calc(220px)] w-full"
            />
            <img
              src={deskBackgroundHome}
              alt="Background"
              className="absolute hidden md:block object-cover object-[25%_35%] h-[calc(220px)] w-full"
            />
          </>
        )}
      </div>
      <main className="relative z-10 mt-[calc(130px)] pt-2 px-2 pb-2 w-full flex justify-center flex-1 text-text h-form">
        <article className="h-full flex flex-col w-[calc(480px)]">
          <div className={`w-full`}>
            <div className="flex items-center">
              <img
                src={`/svg/icon-car-${themeName === 'zapay' ? 'black' : 'white'}.svg`}
                width={24}
                height={24}
                alt="icon car"
              />
              <p className={`ml-1 ${themeName === 'zapay' ? 'text-black' : 'text-white'}`}>{t("pages:home:yourVehicles")}</p>
            </div>
            <div className="flex gap-3 mt-1 mb-6">
              <div className="flex-1 bg-white rounded-xl border border-border p-4 flex items-center gap-3 min-h-[calc(81px)]">
                <p className="text-lg font-bold text-text uppercase">
                  {formattedPlate}
                </p>
              </div>
              <button
                className="bg-white rounded-xl border border-border w-[calc(56px)] h-[calc(81px)] flex items-center justify-center flex-shrink-0"
                type="button"
                onClick={toggleModal}
              >
                <img
                  src={"svg/icon-plus.svg"}
                  width={12}
                  height={17}
                  alt="Adicionar veículo"
                />
              </button>
            </div>

            <div className="bg-background -mx-2 px-2 pt-4 pb-32">
              <h2 className="text-base text-text font-bold mt-4">{t("pages:home:debts")}</h2>
              <div className="w-full h-px bg-border mt-4 mb-3" />
              {items?.length > 0 && (
                <div className="mb-4 mt-7">
                  <div className="flex items-center w-full">
                    <input
                      type="checkbox"
                      name="all"
                      className="w-6 h-6 accent-tertiary"
                      onChange={onChange}
                      checked={items?.length === selectables?.length}
                    />
                    <p className="text-sm text-text ml-6">
                      {t("common:select")}{" "}
                      {items?.length}{" "}
                      {items?.length !== 1 ? t("pages:home:openPassages") : t("pages:home:openPassage")}
                    </p>
                  </div>
                  <div className="w-full h-px bg-border mt-2 mb-2" />
                </div>
              )}
              <div className="flex flex-col gap-3">
                {items?.map((item, index) => (
                  <div key={index} className="border border-foreground rounded-xl p-theme-medium bg-white">
                    <div className="flex items-center w-full">
                      <input
                        type="checkbox"
                        name={item.id}
                        className="w-6 h-6 accent-tertiary"
                        onChange={onChange}
                        checked={
                          selectables?.some(
                            (selectable) => selectable.name === item.id,
                          ) ?? false
                        }
                      />
                      <div className="ml-6 w-full">
                        <div className="flex w-full justify-between">
                          <p className="text-base uppercase">{plate}</p>
                          {item?.hasDate && (
                            <p className="debt-date-badge bg-black text-white text-sm p-1 rounded-lg">
                              {item?.daysLeft > 0
                                ? `${t("common:overdue")} ${item?.daysLeft} ${item?.daysLeft > 1 ? t("common:days") : t("common:day")}`
                                : `${t("common:due")} ${item?.dtVencimento}`
                              }
                            </p>
                          )}
                        </div>
                        <div className="flex w-full justify-between">
                          <p className={`${themeName === 'zapay' ? '' : 'blur-sm'}`}>{`${themeName === 'zapay' ? item?.original?.fmtDataPassagem : t("pages:home:dataUnavailable")}`}</p>
                        </div>
                        <div className="flex w-full justify-between">
                          <p className="text-base">{item?.original?.nmConcessionaria}</p>
                        </div>
                        <div className="flex w-full justify-between">
                          <p className={`font-bold text-base flex-1 mr-2 ${themeName === 'zapay' ? '' : 'blur-sm'}`}>{`${themeName === 'zapay' ? item?.original?.nmPraca : t("pages:home:dataUnavailable")}`}</p>
                          <p className="font-bold text-base whitespace-nowrap">R$ {item?.price}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {items?.length === 0 && (
              <div className="bg-background -mx-2 px-2 pt-4 pb-2">
                <div className="w-full flex justify-center items-center flex-col mt-10">
                  <img
                    src={"/icon-car-front-close.png"}
                    width={48}
                    height={48}
                    alt="Ícone carro"
                    className="mt-6"
                  />
                  <h2 className="mt-1 text-sm text-text text-center px-8">
                    {t("pages:home:passageNotFoundTitle")} <br />
                    {t("pages:home:passageNotFoundDescription")}
                  </h2>
                </div>
                <div className="border-1 border border-border w-full p-4 rounded-theme-medium flex items-center mb-5 mt-20">
                  <img
                    src={"/icon-square.png"}
                    width={40}
                    height={40}
                    alt={t("common:information")}
                    className="h-[calc(40px)] w-[calc(40px)]"
                  />
                  <p className="ml-2 text-text-secondary text-sm">
                    {t("pages:home:passageNotFoundTagDescription")}
                  </p>
                </div>
              </div>
            )}
          </div>
          {items?.length > 0 && <OrgPayment />}
        </article>
      </main>
      {isLoading && <Loading />}
      {isModalOpen && (
        <Modal
          content={
            <div className="flex flex-col">
              <div className="absolute top-3 left-1/2 transform -translate-x-1/2 w-10 h-1 bg-border rounded-full"></div>
              <button
                type="button"
                onClick={toggleModal}
                className="absolute top-6 right-6 text-text"
              >
                <img src="/svg/icon-close.svg" alt="Fechar" className="w-6 h-6" />
              </button>
              <div className="pt-4">
                <h3 className="text-lg font-bold text-text mb-2">
                  {t("pages:home:newPlate")}
                </h3>
                <input
                  value={newPlate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const cleanPlate = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
                    setNewPlate(cleanPlate);
                  }}
                  placeholder={t("pages:home:enterPlate")}
                  minLength={5}
                  maxLength={7}
                  className="w-full text-base text-text bg-transparent border border-border p-3 rounded-lg h-14 mb-4 focus:outline-none focus:border-primary focus:border-2"
                  type="text"
                  autoFocus
                />
                {showCaptcha && (
                  <div className="w-full mb-4 flex justify-center relative">
                    <LoadingOverlay isLoading={isLoadingCaptchaFlag} bgColor="bg-white/50" />
                    {isCaptchaMtcCaptcha ? (
                      <div 
                        className="mtcaptcha" 
                        id={captchaName} 
                        data-callback="onCaptchaSuccess"
                      ></div>
                    ) : (
                      <div
                        className="g-recaptcha mx-auto"
                        data-sitekey={environment.recaptcha.siteKey}
                        data-size="normal"
                        data-theme="light"
                        data-callback="onRecaptchaVerify"
                        data-expired-callback="onRecaptchaExpired"
                        data-error-callback="onRecaptchaError"
                      />
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleSearchClick}
                  disabled={isButtonDisabled}
                  className={buttonClassName}
                >
                  {t("pages:home:searchDebts")}
                </button>
              </div>
            </div>
          }
          actionClose={toggleModal}
        />
      )}
    </div>
  );
}
